import React from 'react';
import { Link } from 'react-router-dom';

import SubtleBadge from 'components/common/SubtleBadge';

import paths from 'routes/paths';
import { formatStatus } from 'utils/valueFormatters';
import {
  getGrnBadgeColor,
  getGrnRejectTypeBadgeColor,
  getGrnType,
  getGrnTypeBadgeColor,
} from 'constants/grn';
import { getGrnSourceInfo } from 'constants/grn/grnType';
import { TIME_OPTION } from 'constants/time';

export const getGrnListColumns = (tenant, warehouse) => [
  {
    accessorKey: 'id',
    header: 'GRN ID',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { id } = original;

      return (
        <Link to={paths.grns.details(tenant, warehouse, id)}>
          <strong>{id}</strong>
        </Link>
      );
    },
  },
  {
    accessorKey: 'grn_status',
    header: 'Status',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => {
      const { grn_status } = original;

      return (
        <SubtleBadge pill bg={getGrnBadgeColor(grn_status)}>
          {formatStatus(grn_status)}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'grn_type',
    header: 'Type',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => {
      const { grn_type } = original;

      return (
        <SubtleBadge pill bg={getGrnTypeBadgeColor(grn_type)}>
          {formatStatus(grn_type)}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'invoice_code',
    header: 'Invoice Code',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { purchase_order } = original;

      return <span>{purchase_order?.invoice_code || '-'}</span>;
    },
  },
  {
    accessorKey: 'po_code',
    header: 'Code',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { sourceCode, sourceDetailPath } = getGrnSourceInfo(
        original,
        tenant,
        warehouse,
      );

      if (!sourceCode) {
        return <span>-</span>;
      }

      return (
        <Link to={sourceDetailPath}>
          <strong>{sourceCode}</strong>
        </Link>
      );
    },
  },
  {
    accessorKey: 'vendor_name',
    header: 'Vendor Name',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { purchase_order } = original;

      return <span>{purchase_order?.vendor?.name || '-'}</span>;
    },
  },
  {
    accessorKey: 'invoice_date',
    header: 'Invoice Date',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { purchase_order } = original;

      return (
        <span>
          {purchase_order?.invoice_date
            ? new Date(purchase_order.invoice_date).toLocaleString(
                'en-IN',
                TIME_OPTION,
              )
            : '-'}
        </span>
      );
    },
  },
  {
    accessorKey: 'modified',
    header: 'Updated',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { modified } = original;

      return (
        <span>{new Date(modified).toLocaleString('en-IN', TIME_OPTION)}</span>
      );
    },
  },
  {
    accessorKey: 'created_by',
    header: 'Created By',
    enableSorting: false,
    meta: {
      meta: {
        valueType: 'string',
      },
    },
    cell: ({ row: { original } }) => {
      const { created_by } = original;

      return <span>{created_by?.email || '-'}</span>;
    },
  },
  {
    accessorKey: 'created',
    header: 'Created',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { created } = original;

      return (
        <span>{new Date(created).toLocaleString('en-IN', TIME_OPTION)}</span>
      );
    },
  },
];

export const GRN_LINE_LIST_COLUMNS = [
  {
    accessorKey: 'id',
    header: 'Vendor SKU',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { po_line } = original;

      return <span>{po_line?.vendor_product?.vendor_sku || '-'}</span>;
    },
  },
  {
    accessorKey: 'total_quantity',
    header: 'Total Qty',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { total_quantity } = original;

      return <span>{total_quantity || 0}</span>;
    },
  },
  {
    accessorKey: 'inventory_type_a',
    header: 'Received (Category A)',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { categories } = original;
      const aType = categories?.find(c => c.inventory_type === 'a');

      return <span>{aType?.quantity || 0}</span>;
    },
  },
  {
    accessorKey: 'inventory_type_b',
    header: 'Received (Category B)',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { categories } = original;
      const bType = categories?.find(c => c.inventory_type === 'b');

      return <span>{bType?.quantity || 0}</span>;
    },
  },
  {
    accessorKey: 'inventory_type_c',
    header: 'Received (Category C)',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { categories } = original;
      const cType = categories?.find(c => c.inventory_type === 'c');

      return <span>{cType?.quantity || 0}</span>;
    },
  },
  {
    accessorKey: 'inventory_type_reject',
    header: 'Rejected',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { categories } = original;
      const rejectType = categories?.find(c => c.inventory_type === 'reject');

      return <span>{rejectType?.quantity || 0}</span>;
    },
  },
  {
    accessorKey: 'reject_type',
    header: 'Reject Type',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => {
      const { reject_type } = original;

      return reject_type ? (
        <SubtleBadge pill bg={getGrnRejectTypeBadgeColor(reject_type)}>
          {formatStatus(reject_type)}
        </SubtleBadge>
      ) : (
        '-'
      );
    },
  },
  {
    accessorKey: 'reject_reason',
    header: 'Reject Reason',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { reject_reason } = original;

      return <span>{reject_reason || '-'}</span>;
    },
  },
  {
    accessorKey: 'expiration_date',
    header: 'Inventory Expiry Date',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { expiration_date } = original;

      return <span>{expiration_date || '-'}</span>;
    },
  },
];
